import { chain, get, map, includes, keys, omit } from 'lodash';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from 'tss-react/mui';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import FileInput from '../FileInput/FileInput';
import signs from '../../Lib/Constants/signatures';

const useStyles = makeStyles()((theme) => ({
  errors: {
    color: get(theme, 'palette.destructive.main'),
    fontSize: '11px',
  },
  formLabel: {
    color: get(theme, 'palette.primary.main'),
    fontSize: '16px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  row: {
    margin: '15px 0px',
  },
  root: {
    padding: '15px 0px',
  },
  select: {},
}));

const SEPARATOR = '*';

export default function ApprovalSignatures(props) {
  const { classes } = useStyles();

  const options = signs.reduce((acc, curr) => {
    acc[curr] = false;
    return acc;
  }, {});

  const [signature, setSignature] = useState(
    chain(props.form.approval_signed_by)
      .split(SEPARATOR)
      .reduce((res, v) => {
        if (!v) return res;
        return { ...res, [v]: true };
      }, options)
      .value(),
  );
  const [signatures, setSignatures] = useState(
    get(props.vendor, 'approval_signatures'),
  );
  const [error, setError] = useState(null);
  const [uploadProgressEvent, setUploadProgressEvent] = useState(null);

  const onChange = (e) => {
    setSignature((s) => {
      const update = { ...s, [e.target.value]: e.target.checked };
      const updateVal = chain(update)
        .omitBy((v) => !v)
        .keys()
        .compact()
        .join('*')
        .value();

      props.onChange({
        target: { value: updateVal, name: 'approval_signed_by' },
      });

      return update;
    });
  };

  useEffect(() => {
    if (includes(keys(signature), 'Greg Scott, CMO')) {
      onChange({ target: { value: 'Greg Scott, CMO', checked: false } });
      onChange({ target: { value: 'Greg Scott, CCO', checked: true } });
      setSignature((s) => {
        const update = omit({ ...s }, 'Greg Scott, CMO');
        return update;
      });
    }
  }, []);

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Vendor Approval Signatures Review
      </Typography>
      <Divider />
      <div className={classes.row}>
        <FormControl
          variant="standard"
          margin="dense"
          fullWidth
          component="fieldset"
          required
        >
          <FormLabel component="legend" className={classes.formLabel}>
            Signed By:
          </FormLabel>
          <div>
            {map(signature, (selected, label) => (
              <FormControlLabel
                label={label}
                key={label}
                control={
                  <Checkbox
                    color="primary"
                    checked={selected}
                    onChange={onChange}
                    name="vendor_signature"
                    value={label}
                    disabled={
                      !includes(keys(options), label) || !props.perms.canUpdate
                    }
                  />
                }
              />
            ))}
          </div>
        </FormControl>
      </div>
      <FileInput
        name="approval_signatures"
        desc="Please upload the vendor approval signatures here"
        header="Approval Signatures"
        suffix="approval-signatures"
        fileLink={get(signatures, 'read', '')}
        disabled={!props.perms.canUpdate}
        exists={get(signatures, 'exists', false)}
        onChange={(_, file) =>
          props
            .onChangeFile(file, props.vendor.id, (progressEvent) =>
              setUploadProgressEvent(progressEvent),
            )
            .then(setSignatures)
            .catch((err) => setError(get(err, 'message')))
        }
        progressEvent={uploadProgressEvent}
        fileType={['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']}
        fullWidth
        required
      />
      {error && <div className={classes.errors}>{error}</div>}
    </div>
  );
}

ApprovalSignatures.propTypes = {
  fileLinks: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({ approval_signed_by: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeFile: PropTypes.func.isRequired,
  vendor: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  perms: PropTypes.shape({
    canRead: PropTypes.bool,
    canUpdate: PropTypes.bool,
    canDelete: PropTypes.bool,
    canCreate: PropTypes.bool,
  }).isRequired,
};
